import { ref } from 'vue'

const PREFERRED_MIME_TYPES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
]

function getSupportedMimeType() {
  if (typeof MediaRecorder === 'undefined') return ''
  return PREFERRED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) || ''
}

export function useHighQualityRecorder({ getStream } = {}) {
  const isPrepared = ref(false)
  let stream = null
  let activeSession = null
  let stopPromise = null

  async function prepare() {
    if (stream?.active) return true
    if (typeof MediaRecorder === 'undefined' || !getStream) return false
    destroy()
    stream = await getStream()
    isPrepared.value = Boolean(stream?.active)
    return isPrepared.value
  }

  function start() {
    if (!stream?.active || activeSession || stopPromise) return false
    const mimeType = getSupportedMimeType()
    const recorder = new MediaRecorder(stream, {
      ...(mimeType ? { mimeType } : {}),
      audioBitsPerSecond: 192000,
    })
    const session = { recorder, chunks: [], startedAt: performance.now() }
    recorder.addEventListener('dataavailable', (event) => {
      if (event.data?.size) session.chunks.push(event.data)
    })
    recorder.start(250)
    activeSession = session
    return true
  }

  function stop() {
    if (stopPromise) return stopPromise
    const session = activeSession
    if (!session || session.recorder.state === 'inactive') return Promise.resolve(null)
    const duration = Math.max(0, (performance.now() - session.startedAt) / 1000)
    stopPromise = new Promise((resolve) => {
      session.recorder.addEventListener('stop', () => {
        const type = session.recorder.mimeType || session.chunks[0]?.type || 'audio/webm'
        const blob = session.chunks.length ? new Blob(session.chunks, { type }) : null
        if (activeSession === session) activeSession = null
        stopPromise = null
        resolve(blob?.size ? { blob, duration } : null)
      }, { once: true })
      session.recorder.stop()
    })
    return stopPromise
  }

  function cancel() {
    if (stopPromise) return
    const session = activeSession
    activeSession = null
    if (session && session.recorder.state !== 'inactive') session.recorder.stop()
  }

  // 流由调用方共享和释放，这里只放开引用
  function destroy() {
    cancel()
    stream = null
    isPrepared.value = false
  }

  return { isPrepared, prepare, start, stop, cancel, destroy }
}
